import { z } from "zod";
import {
  article,
  conditionMatch,
  feed,
  feedCandidate,
  feedError,
  rule,
  ruleAction,
  ruleCondition,
  ruleEvent,
  ruleOutcome,
  ruleTrigger,
  readState,
} from "./entities.js";

/**
 * HTTP contract between web/extension (clients) and the worker (server).
 * Every request body and response is validated against these schemas.
 * The route list in `routes` is the canonical surface->endpoint mapping.
 */

// ---- Requests ----

export const subscribeRequest = z.object({ url: z.string().url() });
export type SubscribeRequest = z.infer<typeof subscribeRequest>;

export const discoverRequest = z.object({
  url: z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), "must be an http(s) URL"),
});
export type DiscoverRequest = z.infer<typeof discoverRequest>;

export const ruleInput = z.object({
  name: z.string().min(1),
  trigger: ruleTrigger.default("new_article"),
  watchedFeedUrl: z.string().url().nullable().default(null),
  conditionMatch: conditionMatch.default("match_all"),
  conditions: z.array(ruleCondition).default([]),
  actions: z.array(ruleAction).min(1),
});
export type RuleInput = z.infer<typeof ruleInput>;

export const setReadRequest = z.object({ read: z.boolean() });
export const setKeptRequest = z.object({ kept: z.boolean() });
export const setEnabledRequest = z.object({ enabled: z.boolean() });
export const removeArticlesRequest = z.object({
  ids: z.array(z.string()).min(1),
});

// ---- Query params (strings coerced) ----

export const articlesQuery = z.object({
  feed: z.string().optional(), // feed id or url
  state: readState.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(), // effectiveDate|id of last row seen
});
export type ArticlesQuery = z.infer<typeof articlesQuery>;

export const searchQuery = z.object({
  q: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ruleEventsQuery = z.object({
  rule: z.string().optional(),
  feed: z.string().optional(),
  outcome: ruleOutcome.optional(),
  since: z.coerce.number().int().optional(),
  until: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

// ---- Responses ----

export const health = z.object({ ok: z.literal(true), version: z.string() });
export const feedList = z.array(feed);
export const feedCandidateList = z.array(feedCandidate);
export const discoverResponse = z.object({ candidates: feedCandidateList });
export type DiscoverResponse = z.infer<typeof discoverResponse>;
export const feedErrorList = z.array(feedError);
export const articleList = z.array(article);
export const ruleList = z.array(rule);
export const ruleEventList = z.array(ruleEvent);
export const apiError = z.object({
  error: z.string(),
  detail: z.string().optional(),
});
export type ApiError = z.infer<typeof apiError>;

/**
 * Canonical route table. Paths are under the API origin; all except
 * GET /api/health require a valid Auth0 bearer token for the owner.
 */
export const routes = {
  health: { method: "GET", path: "/api/health", auth: false },

  listFeeds: { method: "GET", path: "/api/feeds", auth: true },
  subscribe: { method: "POST", path: "/api/feeds", auth: true },
  discover: { method: "POST", path: "/api/feeds/discover", auth: true },
  unsubscribe: { method: "DELETE", path: "/api/feeds/:id", auth: true },
  listFeedErrors: { method: "GET", path: "/api/feeds/:id/errors", auth: true },

  listArticles: { method: "GET", path: "/api/articles", auth: true },
  searchArticles: { method: "GET", path: "/api/articles/search", auth: true },
  keptArticles: { method: "GET", path: "/api/articles/kept", auth: true },
  setRead: { method: "POST", path: "/api/articles/:id/read", auth: true },
  openArticle: { method: "POST", path: "/api/articles/:id/open", auth: true },
  setKept: { method: "POST", path: "/api/articles/:id/keep", auth: true },
  removeArticles: { method: "POST", path: "/api/articles/remove", auth: true },

  listRules: { method: "GET", path: "/api/rules", auth: true },
  createRule: { method: "POST", path: "/api/rules", auth: true },
  updateRule: { method: "PUT", path: "/api/rules/:id", auth: true },
  deleteRule: { method: "DELETE", path: "/api/rules/:id", auth: true },
  setRuleEnabled: {
    method: "POST",
    path: "/api/rules/:id/enabled",
    auth: true,
  },

  listRuleEvents: { method: "GET", path: "/api/rule-events", auth: true },
} as const;

export type RouteName = keyof typeof routes;
